import { SnackbarService } from '../../../_common/client/snackbar-service';
import { trackEvent } from '../../../_common/client/tracking';
import { CustomerReviewBackendRequester } from './customer-review-backend-requester';
import { LocalStorageService } from './local-storage-service';

type HelpfulButtonTrackingDataAttributes = {
    productId: string;
    reviewItemListIndex: string;
    reviewTextLength: string;
    reviewIsVerified: string;
    reviewAgeInDays: string;
    rating: string;
};

export class HelpfulReviewService {
    constructor(
        private readonly customerReviewBackendRequester: CustomerReviewBackendRequester,
        private readonly localStorageService: LocalStorageService,
        private readonly snackbarService: SnackbarService
    ) {}

    public initButtons(rootElement: HTMLElement) {
        const reviewIsAlreadyVotedOn = this.determineIfReviewIsAlreadyVotedOn(rootElement);
        if (reviewIsAlreadyVotedOn) {
            this.hideFeedbackButtons(rootElement);
            return;
        }
        this.initHelpfulButton(rootElement);
        this.initUnhelpfulButton(rootElement);
    }

    private determineIfReviewIsAlreadyVotedOn(rootElement: HTMLElement) {
        const trackingData = this.extractTrackingData(rootElement);
        const reviewId = this.readReviewIdFromHtml(rootElement);
        if (!reviewId) {
            return false;
        }

        return trackingData && this.localStorageService.isReviewAlreadyVotedOn(trackingData.productId, reviewId);
    }

    private initHelpfulButton(rootElement: HTMLElement) {
        const helpfulButton = rootElement.querySelector<HTMLElement>('.js_pdp_cr-item_feedback-button-helpful');
        this.initButton(helpfulButton, rootElement, true);
    }

    private initUnhelpfulButton(rootElement: HTMLElement) {
        const unhelpfulButton = rootElement.querySelector<HTMLElement>('.js_pdp_cr-item_feedback-button-unhelpful');
        this.initButton(unhelpfulButton, rootElement, false);
    }

    private initButton(button: HTMLElement | null, rootElement: HTMLElement, helpful: boolean) {
        const trackingData = this.extractTrackingData(rootElement);
        if (trackingData && button) {
            button.addEventListener('click', () => this.handleHelpfulButtonClick(rootElement, trackingData, helpful));
        }
    }

    private handleHelpfulButtonClick(rootElement: HTMLElement, trackingData: HelpfulButtonTrackingDataAttributes, helpful: boolean) {
        const reviewId = this.readReviewIdFromHtml(rootElement);
        if (reviewId === null) {
            return;
        }

        this.storeHelpfulRatingFrequency();

        if (!this.isRequestAllowedBasedOnFrequency()) return;
        trackEvent(this.createTrackingLabels(trackingData, helpful));
        this.localStorageService.markReviewAsVotedOn(trackingData.productId, reviewId);
        this.hideFeedbackButtons(rootElement);
        this.snackbarService.showSnackbar('Vielen Dank für Dein Feedback!');
        this.updateNumberOfHelpfulVotes(rootElement, helpful);
        this.customerReviewBackendRequester.sendHelpfulRating({
            reviewId,
            productId: trackingData.productId,
            helpful,
        });
    }

    private updateNumberOfHelpfulVotes(rootElement: HTMLElement, helpful: boolean) {
        const helpfulVoteWrapperElement = rootElement.querySelector<HTMLElement>('.js_pdp_cr-item__helpfulSummary');
        const helpfulVoteCountElement = rootElement.querySelector<HTMLElement>('.js_pdp_cr-item__helpfulVoteCount');
        const helpfulVoteTotalCountElement = rootElement.querySelector<HTMLElement>('.js_pdp_cr-item__helpfulVoteTotalCount');

        if (helpfulVoteCountElement === null || helpfulVoteTotalCountElement === null || helpfulVoteWrapperElement === null) {
            return;
        }
        const helpfulVoteCount = parseInt(helpfulVoteCountElement.innerHTML, 10);
        const helpfulVoteTotalCount = parseInt(helpfulVoteTotalCountElement.innerHTML, 10);
        helpfulVoteTotalCountElement.innerHTML = (helpfulVoteTotalCount + 1).toString();

        if (helpful) {
            helpfulVoteCountElement.innerHTML = (helpfulVoteCount + 1).toString();
        }
        helpfulVoteWrapperElement.classList.remove('pl_hidden');
    }

    private isRequestAllowedBasedOnFrequency(): boolean {
        const existingHelpfulRatingFrequency = this.localStorageService.getHelpfulRatingFrequency();
        if (existingHelpfulRatingFrequency == null) {
            return true;
        }
        if (!this.isValidRequestFrequencyStorageValue(existingHelpfulRatingFrequency)) return false;
        const { frequency } = this.parseHelpfulRatingStorageValue(existingHelpfulRatingFrequency);
        return frequency < 100;
    }

    private storeHelpfulRatingFrequency(): void {
        const existingHelpfulRatingFrequency = this.localStorageService.getHelpfulRatingFrequency();
        if (existingHelpfulRatingFrequency == null) {
            this.localStorageService.storeHelpfulRatingFrequency(`${Date.now()},1`);
            return;
        }
        if (!this.isValidRequestFrequencyStorageValue(existingHelpfulRatingFrequency)) return;
        const { timestamp: oldTimestamp, frequency: oldFrequency } = this.parseHelpfulRatingStorageValue(existingHelpfulRatingFrequency);
        this.localStorageService.storeHelpfulRatingFrequency(this.calculateNewFrequencyValue(oldTimestamp, oldFrequency));
    }

    private parseHelpfulRatingStorageValue(helpfulRatingFrequencyStorageValue: string): {
        timestamp: number;
        frequency: number;
    } {
        const [timestamp, frequency] = helpfulRatingFrequencyStorageValue.split(',');
        const parsedFrequency = parseInt(frequency);
        const parsedTimestamp = parseInt(timestamp);
        return { timestamp: parsedTimestamp, frequency: parsedFrequency };
    }

    private calculateNewFrequencyValue(oldTimestamp: number, oldFrequency: number) {
        const timestampTenMinutesAgo = Date.now() - 10 * 60 * 1000;
        const newFrequencyValue = oldTimestamp < timestampTenMinutesAgo ? 1 : oldFrequency + 1;
        return `${Date.now()},${newFrequencyValue}`;
    }

    private isValidRequestFrequencyStorageValue(helpfulRatingFrequencyStorageValue: string): boolean {
        const twoCommaSeperatedIntegersRegex = /^\d+,\d+$/gm;
        return !!helpfulRatingFrequencyStorageValue.match(twoCommaSeperatedIntegersRegex);
    }

    private extractTrackingData(rootElement: HTMLElement): HelpfulButtonTrackingDataAttributes | undefined {
        const { productId, rating, reviewItemListIndex, reviewTextLength, reviewAgeInDays, reviewIsVerified } = rootElement.dataset;

        if (
            productId === undefined ||
            reviewItemListIndex === undefined ||
            reviewTextLength === undefined ||
            reviewIsVerified === undefined ||
            reviewAgeInDays === undefined ||
            rating === undefined
        )
            return;

        return {
            productId,
            reviewItemListIndex,
            reviewTextLength,
            reviewIsVerified,
            reviewAgeInDays,
            rating,
        };
    }

    private createTrackingLabels(trackingData: HelpfulButtonTrackingDataAttributes, helpful: boolean) {
        return {
            review_SingleReviewStars: parseInt(trackingData.rating),
            review_SingleReviewPosition: parseInt(trackingData.reviewItemListIndex) + 1,
            review_SingleReviewTextLength: parseInt(trackingData.reviewTextLength),
            review_SingleReviewEmpty: parseInt(trackingData.reviewTextLength) === 0,
            review_SingleReviewUseful: helpful,
            review_SingleReviewAge: parseInt(trackingData.reviewAgeInDays),
            review_SingleReviewVerified: trackingData.reviewIsVerified === 'true',
        };
    }

    private hideFeedbackButtons(rootElement: HTMLElement) {
        rootElement.querySelector('.pdp_cr-item__feedback-buttons')?.classList.add('pl_hidden');
    }

    private readReviewIdFromHtml(componentRootElement: HTMLElement) {
        return componentRootElement.dataset.reviewId ?? null;
    }
}
